import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import RichTextEditorField from 'components/RichTextEditorField';
import validations from 'validations';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import { trimRichTextValueFromJson } from 'components/RichTextEditorField/utils';

import './BroadcastMessageModal.styles.scss';

const MAX_NUM_OF_CHAR = 100000;

type BroadcastMessageModalProps = {
  className?: string;
  dealRoomId: string;
  onOpenPulseCheck?: () => void;
} & ModalProps;

const BroadcastMessageModal: React.ForwardRefRenderFunction<
  ModalRef,
  BroadcastMessageModalProps
> = (props, ref) => {
  const { className, dealRoomId, onOpenPulseCheck, ...rest } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const classes = classNames('broadcast-message-modal', className);

  const onSubmit = useCallback(
    async ({ text }: { text: string }) => {
      const trimmedText = trimRichTextValueFromJson(text);
      try {
        await api.messages.broadcastMessage(dealRoomId, trimmedText);
        popSuccess(t('broadcastMessageToAllLeadsSuccessfully'));
        modal.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [dealRoomId, modal, t, popSuccess, popServerError],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('broadcastMessageToAllLeads')}
      {...rest}
    >
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting, invalid }) => (
          <form onSubmit={handleSubmit}>
            <p>{t('broadcastMessageToAllLeadsDescription')}</p>

            <RichTextEditorField
              name="text"
              required
              showMaxLength={false}
              maxLength={MAX_NUM_OF_CHAR}
              validate={validations.richEditorRequired()}
            />

            <div className="actions">
              {onOpenPulseCheck && (
                <Button
                  variant="link"
                  className="open-other-modal"
                  onClick={() => {
                    modal?.close();
                    onOpenPulseCheck();
                  }}
                >
                  {t('sendPulseCheckInstead')}
                </Button>
              )}
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={invalid || submitting}>
                {t('send')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(BroadcastMessageModal);
